import { useOutletContext } from 'react-router-dom'

import type { AllFeatureIds } from 'generated/featureIds.generated'
import { TimeReimbursementsPageDocument } from 'generated/resources/graphql'

import { Section } from '@factorialco/deprecated-design-system'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import TimeReimbursements from 'modules/compensations/pages/settings/TimeReimbursements'
import WorkedHoursTransferSetting from 'modules/compensations/pages/settings/TimeReimbursements/WorkedHoursTransferSetting'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FEATURE_IDS: AllFeatureIds[] = ['dev_compensations_worked_hours_transfer_enabled']
const DEFAULT_ALERT_DAY = 15

const TimeReimbursementsPage = () => {
  const { company } = useCurrentSession()
  const { compensationsEnabled } = useOutletContext<{ compensationsEnabled: boolean }>()

  const { data, loading, error } = useQuery(TimeReimbursementsPageDocument, {
    variables: { companyId: company.id },
    skip: !compensationsEnabled,
  })

  if (!compensationsEnabled) return <NotFound />

  const { has_dev_compensations_worked_hours_transfer_enabled: hasWorkedHoursTransferEnabled } =
    checkFeatures(FEATURE_IDS, data?.features?.featuresConnection.nodes)

  const compensationSetting = data?.compensations.compensationSettingsConnection.nodes[0]
  const reimbursements = data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []
  const timeRangeCategories = data?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []
  const workedHoursConcept = payrollConcepts.find((concept) => concept.name === 'worked_hours')
  const canEnableWorkedHoursTransfer =
    workedHoursConcept != null && workedHoursConcept.compensationsStatus?.enabled !== false

  return (
    <ResponsivePage.Body variant="full" showHeader={false} loading={loading} error={error}>
      {hasWorkedHoursTransferEnabled && (
        <Section
          title={i18n.t('compensations.settings.time_reimbursements.worked_hours.title')}
          illustration="clock"
          description={i18n.t(
            'compensations.settings.time_reimbursements.worked_hours.description'
          )}
        >
          <WorkedHoursTransferSetting
            companyId={company.id}
            pendingCompensationsAlertDay={
              compensationSetting?.pendingCompensationsAlertDay ?? DEFAULT_ALERT_DAY
            }
            enabled={compensationSetting?.workedHoursTransferEnabled ?? false}
            canEnable={canEnableWorkedHoursTransfer}
          />
        </Section>
      )}
      <Section.FullWidth
        illustration="Replace"
        title={i18n.t('compensations.settings.time_reimbursements.title')}
        description={i18n.t('compensations.settings.time_reimbursements.description')}
        link={{
          href: helpCenterUrl('payroll_additional_compensations'),
          label: i18n.t('compensations.settings.time_reimbursements.help_text'),
        }}
      >
        <TimeReimbursements
          reimbursements={reimbursements}
          payrollConcepts={payrollConcepts}
          timeRangeCategories={timeRangeCategories}
        />
      </Section.FullWidth>
    </ResponsivePage.Body>
  )
}

export default TimeReimbursementsPage
